import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Flex, Text, Box as FlexBox } from 'rebass/styled-components';
import { Message, MessageBlock, Box, AsyncButton, Button, Modal } from '@getro/rombo';

const Settings = ({ removeCompany, isRemoveCompanyLoading, companySettings, company, history, network }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(false);
  }, [companySettings.isRemoved]);

  if (!network.isManager) {
    return null;
  }
  return (
    <Box title="Remove from your job board" data-testid="company_settings">
      <Flex
        fontSize="14px"
        sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}
      >
        <Text as="p">
          Remove the <b>{company.name}</b> profile and associated jobs from your job board.
        </Text>
        <Button onClick={() => setIsOpen(true)} data-testid="modal_trigger" variant="danger" sx={{ flexShrink: 0 }}>
          Remove company
        </Button>
      </Flex>
      {isOpen && (
        <Modal
          data-testid="delete_confirmation"
          open={isOpen}
          onCancel={() => setIsOpen(false)}
          onOpen={() => setIsOpen(true)}
          title={`Remove ${company.name} from ${network.name}`}
          actions={
            <Flex justifyContent={['flex-end']}>
              <Button onClick={() => setIsOpen(false)} variant="secondary" mr={2}>
                No, keep company
              </Button>

              <AsyncButton
                variant="danger"
                loading={isRemoveCompanyLoading}
                onClick={() => {
                  removeCompany({ networkId: network.id, company });
                }}
                ml="8px"
                data-testid="modal_remove_company"
              >
                Yes, remove company
              </AsyncButton>
            </Flex>
          }
          size="small"
        >
          <Flex fontSize={1} flexDirection="column">
            <FlexBox width={[1]} mb={2}>
              <Message type="warning">
                <Text fontSize={1}>Please pay close attention to the following:</Text>
              </Message>
            </FlexBox>
            <FlexBox mt={2} width={[1]}>
              <Flex pl={3} as="ul" flexDirection="column">
                <FlexBox as="li" variant="listItem" pt={0}>
                  This company profile will be removed from your job board.
                </FlexBox>
                <FlexBox as="li" variant="listItem" pt={0}>
                  New jobs at <b>{company.name}</b> will no longer appear on the job board. Existing jobs will be
                  removed.
                </FlexBox>
                <FlexBox as="li" variant="listItem" pt={0}>
                  Related company and job data will only be included in your analytics during the timeframe{' '}
                  <b>{company.name}</b> was part of your network.
                </FlexBox>
                <FlexBox as="li" variant="listItem" pt={0}>
                  All historic hires associated with <b>{company.name}</b> will be permanently removed from the system.
                </FlexBox>
                <FlexBox as="li" variant="listItem" pt={0}>
                  Company admins of <b>{company.name}</b> will not be able to manually post jobs to the job board.
                </FlexBox>
              </Flex>
            </FlexBox>
            {companySettings.error && !!companySettings.error.length && (
              <FlexBox data-testid="modal_error_message" width={[1]}>
                <Message type="error" className="Box__error-message">
                  <Text fontSize={2}>{companySettings.error}</Text>
                </Message>
              </FlexBox>
            )}
          </Flex>
        </Modal>
      )}

      {companySettings.isRemoved && (
        <Modal data-testid="confirmation_message" open={companySettings.isRemoved} simple>
          <MessageBlock
            type="success"
            title={`${company.name} has been removed from your job board`}
            buttonText="Go to companies"
            onClick={() => history.push(`/networks/${network.slug}/companies`)}
          />
        </Modal>
      )}
    </Box>
  );
};

Settings.propTypes = {
  removeCompany: PropTypes.func,
  company: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  network: PropTypes.object.isRequired,
  companySettings: PropTypes.object.isRequired,
  isRemoveCompanyLoading: PropTypes.bool.isRequired,
};

Settings.defaultProps = {
  removeCompany: () => {},
};
export default Settings;
